
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Card } from '../../ui/Card';
import { Bot, Zap, ArrowRight, CheckCircle } from 'lucide-react';
import { Token } from '../../../types';

interface AiAnalyticsPanelProps {
    fromToken: Token | null;
    toToken: Token | null;
    amount: number;
}

export const AiAnalyticsPanel: React.FC<AiAnalyticsPanelProps> = ({ fromToken, toToken, amount }) => {
    return (
        <Card className="border-nobel-gold/20 bg-nobel-gold/[0.01] dark:bg-nobel-gold/[0.02] relative overflow-hidden group p-6 sm:p-7 transition-all">
            <div className="absolute top-0 right-0 p-4 opacity-[0.03] group-hover:opacity-[0.06] transition-opacity pointer-events-none">
                <Bot size={140} className="rotate-12" />
            </div>

            <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-nobel-gold flex items-center justify-center text-black shadow-lg shadow-nobel-gold/20">
                        <Bot size={20} />
                    </div>
                    <div>
                        <h3 className="font-serif text-lg font-bold text-[#0A0B0F] dark:text-white leading-tight">Ghost Intelligence</h3>
                        <p className="text-[9px] text-stone-500 uppercase tracking-widest font-black">Transaction Analysis</p>
                    </div>
                </div>

                <div className="space-y-4">
                    {amount > 0 ? (
                        <>
                            <div className="p-4 bg-white dark:bg-black/40 rounded-xl border border-nobel-gold/10 space-y-3 shadow-sm">
                                <div className="flex items-start gap-3">
                                    <Zap size={16} className="text-nobel-gold mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-[11px] font-bold text-[#0A0B0F] dark:text-white uppercase tracking-wider">Optimal Route Detected</div>
                                        <p className="text-[10px] text-stone-500 leading-relaxed mt-1.5 font-medium">
                                            GhostOS detected a high-liquidity pool on {fromToken?.chain === 'SOL' ? 'Jupiter V6' : 'Cetus Protocol'}. Execution will bypass high-slippage routers to preserve ~0.15% in volume value.
                                        </p>
                                    </div>
                                </div>
                                <button className="w-full py-2 bg-nobel-gold text-black text-[9px] font-black uppercase tracking-[0.2em] rounded-lg hover:bg-[#D4B06A] transition-all flex items-center justify-center gap-2 shadow-md">
                                    Enabled Optimized Routing <ArrowRight size={10} strokeWidth={3} />
                                </button>
                            </div>

                            <div className="p-4 bg-white dark:bg-black/20 rounded-xl border border-emerald-500/10 flex items-start gap-3 shadow-sm">
                                <div className="p-1 rounded-full bg-emerald-500/10 shrink-0">
                                    <CheckCircle size={14} className="text-emerald-500" />
                                </div>
                                <div>
                                    <div className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">Volatility Check: Stable</div>
                                    <p className="text-[10px] text-stone-500 leading-relaxed mt-1 font-medium">
                                        Market depth for {toToken?.symbol} is optimal. Buy/Sell pressure is balanced across tracked block headers.
                                    </p>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="py-10 text-center flex flex-col items-center">
                            <div className="w-12 h-12 rounded-full bg-stone-100 dark:bg-white/5 flex items-center justify-center text-stone-300 dark:text-stone-600 mb-4 border border-stone-200 dark:border-white/10 shadow-inner">
                                <Zap size={20} className="animate-pulse" />
                            </div>
                            <p className="text-[10px] text-stone-400 font-bold uppercase tracking-[0.2em] max-w-[180px]">Awaiting Transaction Parameters</p>
                            <p className="text-[11px] text-stone-500 mt-2 italic px-4 leading-relaxed">Input an amount to compute optimal cross-chain settlement routes.</p>
                        </div>
                    )}
                </div>

                <div className="mt-8 pt-6 border-t border-stone-100 dark:border-white/5 flex justify-between items-center px-1">
                    <span className="text-[9px] font-bold text-stone-400 uppercase tracking-[0.2em]">Computation Node</span>
                    <span className="text-[9px] font-mono font-bold text-emerald-500 bg-emerald-500/5 px-2 py-0.5 rounded border border-emerald-500/10 uppercase animate-pulse">Live Analysis</span>
                </div>
            </div>
        </Card>
    );
};
